import { apiService } from "@/services";
import { useMutation, useQuery } from "@tanstack/react-query";
import queryClient from "@/query-client";
import type { BrandSignal } from "@/data/mockBrandSignalsData";
import { DEFAULT_STALE_TIME } from "@/constants";

export const brandSignalsQueryKey = (brandId: number | null | undefined) =>
  ["brand", "brand-signals", brandId] as const;

export const useGetBrandSignals = (brandId: number | null | undefined) => {
  return useQuery({
    queryKey: brandSignalsQueryKey(brandId),
    queryFn: () => apiService.get<BrandSignal[]>(`/brands/${brandId}/brand-signals`),
    enabled: !!brandId,
    staleTime: DEFAULT_STALE_TIME,
  });
};

export type CreateBrandSignalBody = {
  hypothesis: string;
  category: string;
  products?: string[];
};

export const useCreateBrandSignal = () => {
  return useMutation({
    mutationFn: ({ brandId, body }: { brandId: number; body: CreateBrandSignalBody }) =>
      apiService.post<BrandSignal, CreateBrandSignalBody>(`/brands/${brandId}/brand-signals`, body),
    onSuccess: (_data, { brandId }) => {
      queryClient.invalidateQueries({ queryKey: brandSignalsQueryKey(brandId) });
    },
  });
};

export const useDeleteBrandSignal = () => {
  return useMutation({
    mutationFn: ({ brandId, signalId }: { brandId: number; signalId: string }) =>
      apiService.delete<{ message: string }>(`/brands/${brandId}/brand-signals/${signalId}`),
    onSuccess: (_data, { brandId }) => {
      queryClient.invalidateQueries({ queryKey: brandSignalsQueryKey(brandId) });
    },
  });
};

export const useUpdateBrandSignalTags = () => {
  return useMutation({
    mutationFn: ({ brandId, signalId, tags }: { brandId: number; signalId: string; tags: string[] }) =>
      apiService.patch<BrandSignal, { tags: string[] }>(`/brands/${brandId}/brand-signals/${signalId}/tags`, { tags }),
    onSuccess: (_data, { brandId }) => {
      queryClient.invalidateQueries({ queryKey: brandSignalsQueryKey(brandId) });
    },
  });
};
